/**
 * Access-control helpers for view-only users.
 *
 * The token endpoint (`/api/token/`, `/api/auth/token/login/`) reports
 * `view_only: true` for an account that must not create, update or delete
 * anything. The flag is read from the login response body, falling back to the
 * `view_only` claim on the access JWT, so either shape works.
 *
 * Backend enforces read-only globally: any POST/PUT/PATCH/DELETE from a
 * view-only user is rejected with `403 { error: "VIEW_ONLY_ACCESS" }` before
 * it reaches the endpoint. These helpers only drive the UX — hiding write
 * controls and reacting to a blocked write.
 *
 * View-only is a HARD restriction: it wins over whatever groups the user
 * holds. That is how the backend behaves, so the UI must match it.
 */
import jwt from "jsonwebtoken";

// Field name on the token response / JWT payload.
export const VIEW_ONLY_CLAIM = "view_only";

// localStorage key used to survive a page reload.
export const VIEW_ONLY_STORAGE_KEY = "view_only";

// Error code returned by the backend middleware for a blocked write.
export const VIEW_ONLY_ERROR = "VIEW_ONLY_ACCESS";

const isTrue = (value) => value === true || value === "true";

/**
 * @returns {boolean|null} the `view_only` claim on the token, or null when the
 *   token carries no such claim (so callers can fall back instead of assuming
 *   write access).
 */
export const readViewOnlyClaim = (token) => {
  if (!token) return null;
  let payload = null;
  try {
    payload = jwt.decode(token);
  } catch (e) {
    return null;
  }
  if (!payload || !(VIEW_ONLY_CLAIM in payload)) return null;
  return isTrue(payload[VIEW_ONLY_CLAIM]);
};

/**
 * Resolve view-only for a token API response: response body first, then the
 * JWT claim.
 */
export const resolveViewOnly = (response, token) => {
  if (response && VIEW_ONLY_CLAIM in response) return isTrue(response[VIEW_ONLY_CLAIM]);
  const claim = readViewOnlyClaim(token);
  return claim === null ? false : claim;
};

/** Persisted flag, read back on reload (falls back to the stored JWT claim). */
export const getStoredViewOnly = () => {
  const stored = localStorage.getItem(VIEW_ONLY_STORAGE_KEY);
  if (stored !== null) return isTrue(stored);
  const claim = readViewOnlyClaim(localStorage.getItem("jwt"));
  return claim === null ? false : claim;
};

export const storeViewOnly = (viewOnly) =>
  localStorage.setItem(VIEW_ONLY_STORAGE_KEY, isTrue(viewOnly) ? "true" : "false");

export const clearStoredViewOnly = () =>
  localStorage.removeItem(VIEW_ONLY_STORAGE_KEY);

/** @param {boolean|string} viewOnly - the `view_only` flag off auth state. */
export const isViewOnly = (viewOnly) => isTrue(viewOnly);

/** Options for the Access Level dropdown on the user add / edit forms. */
export const ACCESS_LEVEL_OPTIONS = [
  { value: false, label: "Full Access" },
  { value: true, label: "View Only" },
];

/** The option matching a `view_only` value, for a controlled <Select>. */
export const accessLevelOption = (viewOnly) =>
  ACCESS_LEVEL_OPTIONS.find((opt) => opt.value === isTrue(viewOnly));

/** Redux selector form: selectIsViewOnly(state) */
export const selectIsViewOnly = (state) =>
  isTrue(state && state.authUser ? state.authUser.viewOnly : false);
